import React, { useCallback, useMemo } from 'react';
import {
    CalendarCheck,
    CalendarClock,
    CalendarPlus,
    Clock,
    Info,
    InfoIcon,
    Mail,
    PlayCircle,
    UserCog,
    UserRoundPlus,
} from 'lucide-react';
import { Button } from '@shadcn/ui/components/ui/button';
import { InfoBoxContainer, InfoBoxVariant } from './info-box-container';
import { DisabledPrimaryActionReason, useMessageComposer } from '../../../../../hooks/messaging/use-message-composer';
import { useSubscription } from '@apollo/client';
import { PROSPECT_FUTURE_ACTIONS_SUBSCRIPTION, PROSPECT_UNSCHEDULED_ACTIONS_SUBSCRIPTION } from '@zaplify/graphql';
import { useLinkedin } from '../../../../../hooks/use-linkedin';
import { ChannelType } from '../../../../../types/channel-type';
import { extensionLinkURL } from '../../../../../constants/extension';
import { useNavigate } from 'react-router-dom';
import { useEditContactDetailsDialog } from '../../../../../components/dialogs/edit-contact-details-dialog';
import { useActions } from '../../../../../hooks/use-actions';
import { useSnoozeDialog } from '../../../../../components/dialogs/snooze-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMessageComposerV2 } from '../../../../../hooks/messaging/message-composer/use-message-composer-v2';

dayjs.extend(utc);

interface InfoBoxProps {}

export const MessageComposerInfoBoxV2: React.FC<InfoBoxProps> = () => {
    const { prospectId, bodyLength, handleChannelTypeChange, isSending, disabledPrimaryActionReason } =
        useMessageComposerV2();
    const { isPremium, fetchLatestPremiumStatus } = useLinkedin();
    const { openEditContactDetailsDialog } = useEditContactDetailsDialog();
    const navigate = useNavigate();

    const navigateToConnectChannelAccount = () => navigate('/new/account-management/apps');

    // const hasLoaded = useMemo(() => {
    //     return !nextActionLoading && !futureActionsLoading && !unscheduledActionsLoading;
    // }, [nextActionLoading, futureActionsLoading, unscheduledActionsLoading]);
    const hasLoaded = true;

    let variant: InfoBoxVariant = 'brand';
    let content: React.ReactNode | null = null;

    if (!hasLoaded) {
        return null;
    }

    if (isSending) {
        return null;
    }

    switch (disabledPrimaryActionReason) {
        case DisabledPrimaryActionReason.LINKEDIN_NOT_SUPPORTED_ON_MOBILE:
            variant = 'warning';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Info className="h-5 w-5 stroke-1 shrink-0" />
                        <p>LinkedIn messages are not supported on mobile</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleChannelTypeChange(ChannelType.EMAIL)}>
                        Switch to email
                    </Button>
                </>
            );
            break;
        case DisabledPrimaryActionReason.EXTENSION_NOT_INSTALLED:
            variant = 'warning';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Info className="h-5 w-5 stroke-1 shrink-0" />
                        <p>Looks like the Andsend web extension isn't installed. Install to proceed.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => window.open(extensionLinkURL, '_blank')}>
                        Go to store
                    </Button>
                </>
            );
            break;
        case DisabledPrimaryActionReason.LINKEDIN_MISSING_CONFIG:
            variant = 'warning';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Info className="h-5 w-5 stroke-1 shrink-0" />
                        <p>Connect your LinkedIn account to enable LinkedIn messaging</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={navigateToConnectChannelAccount}>
                        Connect LinkedIn
                    </Button>
                </>
            );
            break;
        case DisabledPrimaryActionReason.LINKEDIN_NOT_AUTHENTICATED:
            variant = 'warning';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Info className="h-5 w-5 stroke-1 shrink-0" />
                        <p>Please sign in to LinkedIn to continue.</p>
                    </div>
                </>
            );
            break;
        case DisabledPrimaryActionReason.RECIPIENT_MISSING_LINKEDIN_PROFILE:
            variant = 'blue';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <UserCog className="h-5 w-5 stroke-1 shrink-0" />
                        <p>Contact is missing a valid LinkedIn URL. Add URL or switch to email.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openEditContactDetailsDialog(prospectId)}>
                        Add URL
                    </Button>
                </>
            );
            break;
        case DisabledPrimaryActionReason.RECIPIENT_CONNECTION_PENDING:
            variant = 'blue';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Clock className="h-5 w-5 stroke-1 shrink-0" />
                        <p>You can't send LinkedIn messages until your connection request is accepted.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleChannelTypeChange(ChannelType.EMAIL)}>
                        Switch to email
                    </Button>
                </>
            );
            break;
        case DisabledPrimaryActionReason.MESSAGE_TOO_LONG:
            variant = 'blue';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <UserRoundPlus className="h-5 w-5 stroke-1 shrink-0" />
                        <p>{`LinkedIn requires connection requests to be under ${
                            isPremium ? 300 : 200
                        } characters (${bodyLength}/${isPremium ? 300 : 200})`}</p>
                    </div>
                    {!isPremium && (
                        <Button variant="outline" size="sm" onClick={async () => await fetchLatestPremiumStatus()}>
                            I have premium
                        </Button>
                    )}
                </>
            );
            break;
        case DisabledPrimaryActionReason.MESSAGE_EMPTY:
            break;
        case DisabledPrimaryActionReason.SUBJECT_LINE_EMPTY:
            break;
        case DisabledPrimaryActionReason.EMAIL_NOT_CONNECTED:
            variant = 'warning';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Mail className="h-5 w-5 stroke-1 shrink-0" />
                        <p>Connect your email account to send and receive emails.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={navigateToConnectChannelAccount}>
                        Connect email
                    </Button>
                </>
            );
            break;
        case DisabledPrimaryActionReason.EMAIL_NOT_AUTHENTICATED:
            variant = 'warning';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Info className="h-5 w-5 stroke-1 shrink-0" />
                        <p>Andsend has lost connection to your email account. Please reconnect.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={navigateToConnectChannelAccount}>
                        Connect email
                    </Button>
                </>
            );

            break;
        case DisabledPrimaryActionReason.EMAIL_SCOPES:
            variant = 'warning';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Info className="h-5 w-5 stroke-1 shrink-0" />
                        <p>Andsend does not have the necessary permissions to send emails. Please reconnect.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={navigateToConnectChannelAccount}>
                        Connect email
                    </Button>
                </>
            );
            break;

        case DisabledPrimaryActionReason.RECIPIENT_INVALID_EMAIL:
            variant = 'warning';
            content = (
                <>
                    <div className="flex items-center gap-2 grow w-full">
                        <Mail className="h-5 w-5 stroke-1 shrink-0" />
                        <p>This contact has no email address. Add it to send them an email.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openEditContactDetailsDialog(prospectId)}>
                        Add email
                    </Button>
                </>
            );

            break;
    }

    if (content) {
        return (
            <InfoBoxContainer key={prospectId} variant={variant}>
                {content}
            </InfoBoxContainer>
        );
    }

    return null;
};
